// https://github.com/jkuri/ng2-datepicker/tree/master/src/ng2-datepicker
import {
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Inject,
  Input,
  OnInit,
  Output,
  SimpleChanges,
} from "@angular/core";
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from "@angular/forms";
import {ISlimScrollOptions, SlimScrollOptions} from "ngx-slimscroll";
import * as moment from "moment";

const Moment: any = (moment as any).default || moment;

export interface IDateModel { // eslint-disable-line
  day: string;
  month: string;
  year: string;
  formatted: string;
  momentObj: moment.Moment;
}

export class DateModel {
  day: string;
  month: string;
  year: string;
  formatted: string;
  momentObj: moment.Moment;

  constructor(obj?: IDateModel) {
    this.day = obj && obj.day ? obj.day : null;
    this.month = obj && obj.month ? obj.month : null;
    this.year = obj && obj.year ? obj.year : null;
    this.formatted = obj && obj.formatted ? obj.formatted : null;
    this.momentObj = obj && obj.momentObj ? obj.momentObj : null;
  }
}

export interface IDatePickerOptions { // eslint-disable-line
  autoApply?: boolean;
  style?: "normal" | "big" | "bold";
  locale?: string;
  minDate?: Date;
  maxDate?: Date;
  initialDate?: Date;
  firstWeekdaySunday?: boolean;
  format?: string;
  selectYearText?: string;
  todayText?: string;
  clearText?: string;
  minView?: "year" | "month" | "day";
}

export class DatePickerOptions {
  autoApply?: boolean;
  style?: "normal" | "big" | "bold";
  locale?: string;
  minDate?: Date;
  maxDate?: Date;
  initialDate?: Date;
  firstWeekdaySunday?: boolean;
  format?: string;
  selectYearText?: string;
  todayText?: string;
  clearText?: string;
  minView?: "year" | "month" | "day";

  constructor(obj?: IDatePickerOptions) {
    this.autoApply = (obj && obj.autoApply === true);
    this.style = obj && obj.style ? obj.style : "normal";
    this.locale = obj && obj.locale ? obj.locale : "en";
    this.minDate = obj && obj.minDate ? obj.minDate : null;
    this.maxDate = obj && obj.maxDate ? obj.maxDate : null;
    this.initialDate = obj && obj.initialDate ? obj.initialDate : null;
    this.firstWeekdaySunday = !obj || obj.firstWeekdaySunday === undefined ? true : !!obj.firstWeekdaySunday;
    this.format = obj && obj.format ? obj.format : "YYYY-MM-DD";
    this.selectYearText = obj && obj.selectYearText ? obj.selectYearText : "Select Year";
    this.todayText = obj && obj.todayText ? obj.todayText : "Today";
    this.clearText = obj && obj.clearText ? obj.clearText : "Clear";
    this.minView = obj && obj.minView ? obj.minView : "day";
  }
}

export interface CalendarDate {
  day: number;
  month: number;
  year: number;
  enabled: boolean;
  today: boolean;
  selected: boolean;
  momentObj: moment.Moment;
}

export const CALENDAR_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => DatePickerComponent),
  multi: true,
};

@Component({
  selector: "datepicker",
  templateUrl: "./datepicker.template.pug",
  providers: [CALENDAR_VALUE_ACCESSOR],
})
export class DatePickerComponent implements ControlValueAccessor, OnInit {
  @Input() options: DatePickerOptions;
  @Input() inputEvents: EventEmitter<{type: string, data: string | DateModel}>;
  @Output() outputEvents: EventEmitter<{type: string, data: string | DateModel}>;

  date: DateModel;

  opened: boolean;
  currentDate: moment.Moment;
  days: CalendarDate[];
  months: any[];
  monthPicker: boolean;
  years: number[];
  yearPicker: boolean;
  scrollOptions: SlimScrollOptions;
  weekdaysText: string[];

  minDate: moment.Moment | any;
  maxDate: moment.Moment | any;

  invalidDate: boolean;

  private onTouchedCallback: () => void = () => {};
  private onChangeCallback: (_: any) => void = () => {};

  fixWeekdaysText() {
    this.weekdaysText = this.options.firstWeekdaySunday ?
      ["S", "M", "T", "W", "T", "F", "S"] :
      ["M", "T", "W", "T", "F", "S", "U"];
  }

  constructor(@Inject(ElementRef) public el: ElementRef) {
    this.opened = false;
    this.currentDate = Moment();
    this.options = this.options || {};
    this.fixWeekdaysText();

    this.days = [];
    this.years = [];
    this.date = new DateModel({
      day: null,
      month: null,
      year: null,
      formatted: null,
      momentObj: null,
    });

    this.outputEvents = new EventEmitter<{type: string, data: string | DateModel}>();

    if (!this.inputEvents) {
      return;
    }

    this.inputEvents.subscribe((event: {type: string, data: string | DateModel}) => {
      if (event.type === "setDate") {
        this.date = event.data as DateModel;
        this.value = this.date.formatted;
      } else if (event.type === "default") {
        if (event.data === "open") {
          this.open();
        } else if (event.data === "close") {
          this.close();
        }
      }
    });
  }

  @Input()
  get value(): string {
    return this.date.formatted;
  }

  set value(date: string) {
    if (!date) {
      return;
    }
    let momentObj = moment.utc(date, this.options.format).startOf("day");
    this.invalidDate = !momentObj.isValid();
    const dayFormat = date.substring(8, date.length);
    if (this.invalidDate || Number.isNaN(parseInt(dayFormat, 10))) {
      momentObj = this.currentDate;
      this.invalidDate = true;
    }
    this.date = new DateModel({
      day: momentObj.format("DD"),
      month: momentObj.format("MM"),
      year: momentObj.format("YYYY"),
      formatted: momentObj.format("YYYY-MM-DD"),
      momentObj,
    });
    try {
      this.onChangeCallback(date);
    } catch (e) {
      if (/There is no FormControl instance/i.test(e.message)) {
        console.debug(e);
      } else {
        throw e;
      }
    }
    this.outputEvents.emit({type: "dateChanged", data: this.value});
  }

  ngOnInit() {
    this.options = new DatePickerOptions(this.options);
    this.fixWeekdaysText();
    this.scrollOptions = new SlimScrollOptions({
      barBackground: "#C9C9C9",
      barWidth: "7",
      gridBackground: "#C9C9C9",
      gridWidth: "2",
      scrollBar: false,
    } as ISlimScrollOptions);

    if (this.options.initialDate instanceof Date) {
      this.currentDate = Moment(this.options.initialDate);
      this.selectDate(null, this.currentDate);
    }

    if (this.options.minDate instanceof Date) {
      this.minDate = Moment(this.options.minDate);
    } else {
      this.minDate = null;
    }

    if (this.options.maxDate instanceof Date) {
      this.maxDate = Moment(this.options.maxDate);
    } else {
      this.maxDate = null;
    }

    this.generateYears();
    this.generateMonths();
    this.generateCalendar();
    this.yearPicker = this.isYearPicker();
    this.monthPicker = this.isMonthPicker();
    this.outputEvents.emit({type: "default", data: "init"});

    if (typeof window !== "undefined") {
      const body = document.querySelector("body");
      body.addEventListener(
        "click",
        (e) => {
          if (!this.opened || !e.target) {
            return;
          }
          if (this.el.nativeElement !== e.target && !this.el.nativeElement.contains((e.target as any))) {
            this.close();
          }
        },
        false,
      );
    }

    if (this.inputEvents) {
      this.inputEvents.subscribe((e: any) => {
        if (e.type === "action") {
          if (e.data === "toggle") {
            this.toggle();
          }
          if (e.data === "close") {
            this.close();
          }
          if (e.data === "open") {
            this.open();
          }
        }

        if (e.type === "setDate") {
          if (!(e.data instanceof Date)) {
            throw new Error("Input data must be an instance of Date!");
          }
          const date: moment.Moment = Moment(e.data);
          if (!date) {
            throw new Error(`Invalid date: ${e.data}`);
          }
          this.date = new DateModel({
            day: date.format("DD"),
            month: date.format("MM"),
            year: date.format("YYYY"),
            formatted: date.format(this.options.format),
            momentObj: date,
          });
          this.value = this.date.formatted;
        }
      });
    }
  }

  generateCalendar() {
    const date: moment.Moment = Moment(this.currentDate);
    const month = date.month();
    const year = date.year();
    let n = 1;
    const firstWeekDay = (this.options.firstWeekdaySunday) ? date.date(2).day() : date.date(1).day();

    if (firstWeekDay !== 1) {
      n -= (firstWeekDay + 6) % 7;
    }

    this.days = [];
    const selectedDate: moment.Moment = this.date.momentObj;
    for (let i = n; i <= date.endOf("month").date(); i += 1) {
      const currentDate: moment.Moment = Moment(`${i}.${month + 1}.${year}`, "DD.MM.YYYY");
      const today: boolean = (Moment().isSame(currentDate, "day") && Moment().isSame(currentDate, "month"));
      const selected: boolean = !!(selectedDate && selectedDate.isSame(currentDate, "day"));
      let betweenMinMax = true;

      if (this.minDate !== null) {
        if (this.maxDate !== null) {
          betweenMinMax = currentDate.isBetween(this.minDate, this.maxDate, "day", "[]");
        } else {
          betweenMinMax = !currentDate.isBefore(this.minDate, "day");
        }
      } else if (this.maxDate !== null) {
        betweenMinMax = !currentDate.isAfter(this.maxDate, "day");
      }

      const day: CalendarDate = {
        day: i > 0 ? i : null,
        month: i > 0 ? month : null,
        year: i > 0 ? year : null,
        enabled: i > 0 ? betweenMinMax : false,
        today: i > 0 && today,
        selected: i > 0 && selected,
        momentObj: currentDate,
      };

      this.days.push(day);
    }
  }

  selectDate(e: MouseEvent, date: moment.Moment) {
    if (e) {
      e.preventDefault();
    }

    setTimeout(() => {
      this.date = new DateModel({
        day: date.format("DD"),
        month: date.format("MM"),
        year: date.format("YYYY"),
        formatted: date.format(this.options.format),
        momentObj: date,
      });
      this.value = this.date.formatted;
      this.generateCalendar();
    });

    this.opened = false;
  }

  selectMonth(e: MouseEvent, month: number) {
    e.preventDefault();

    setTimeout(() => {
      const date: moment.Moment = this.currentDate.month(month);
      this.date = new DateModel({
        day: date.format("DD"),
        month: date.format("MM"),
        year: date.format("YYYY"),
        formatted: date.format(this.options.format),
        momentObj: date,
      });
      this.value = this.date.formatted;
      this.monthPicker = this.isMonthPicker();
      this.generateCalendar();
    });
    this.opened = !this.isMonthPicker();
  }

  selectYear(e: MouseEvent, year: number) {
    e.preventDefault();

    setTimeout(() => {
      const date: moment.Moment = this.currentDate.year(year);
      this.date = new DateModel({
        day: date.format("DD"),
        month: date.format("MM"),
        year: date.format("YYYY"),
        formatted: date.format(this.options.format),
        momentObj: date,
      });
      this.value = this.date.formatted;
      this.yearPicker = this.isYearPicker();
      this.monthPicker = !this.isYearPicker();
      this.generateMonths();
      this.generateCalendar();
    });
    this.opened = !this.isYearPicker();
  }

  generateMonths() {
    const year = this.currentDate.year();

    this.months = moment.monthsShort().map((month, index) => {
      let enabled = true;
      const currentDate: moment.Moment = Moment(`15.${index + 1}.${year}`, "DD.MM.YYYY");
      if (this.minDate !== null) {
        if (this.maxDate !== null) {
          enabled = currentDate.isBetween(this.minDate, this.maxDate, "month", "[]");
        } else {
          enabled = currentDate.isSameOrAfter(this.minDate, "month");
        }
      } else if (this.maxDate !== null) {
        enabled = currentDate.isSameOrBefore(this.maxDate, "month");
      }
      return {
        month,
        index,
        enabled,
        today: moment().isSame(currentDate, "month"),
        selected: currentDate.isSame(this.currentDate, "month"),
      };
    });
  }

  generateYears() {
    const date: moment.Moment = this.minDate ? moment(this.minDate) : Moment().year(Moment().year() - 40);
    const toDate: moment.Moment = this.maxDate || Moment().year(Moment().year() + 40);
    const years = toDate.year() - date.year();

    this.years = [];
    for (let i = 0; i < years; i++) {
      this.years.push(date.year());
      date.add(1, "year");
    }
  }

  writeValue(dateString: string) {
    if (!dateString) {
      return;
    }
    this.value = dateString;
  }

  registerOnChange(fn: any) {
    this.onChangeCallback = fn;
  }

  registerOnTouched(fn: any) {
    this.onTouchedCallback = fn;
  }

  prevMonth() {
    this.currentDate = this.currentDate.subtract(1, "month");
    this.generateCalendar();
  }

  nextMonth() {
    this.currentDate = this.currentDate.add(1, "month");
    this.generateCalendar();
  }

  today() {
    this.currentDate = Moment();
    this.selectDate(null, this.currentDate);
  }

  toggle() {
    this.opened = !this.opened;
    if (this.opened) {
      this.onOpen();
    }

    this.outputEvents.emit({type: "default", data: "opened"});
    if (this.date && this.date.momentObj) {
      this.currentDate.month(this.date.momentObj.month());
      this.currentDate.year(this.date.momentObj.year());
      this.generateCalendar();
    }
  }

  open() {
    this.opened = true;
    this.onOpen();
  }

  close() {
    this.opened = false;
    this.outputEvents.emit({type: "default", data: "closed"});
  }

  onOpen() {
    this.yearPicker = false;
  }

  openYearPicker() {
    setTimeout(() => {
      this.monthPicker = false;
      this.yearPicker = true;
    });
  }

  openMonthPicker() {
    this.generateMonths();
    setTimeout(() => { this.monthPicker = true; });
  }

  clearDate() {
    this.date = new DateModel({
      day: null,
      month: null,
      year: null,
      formatted: null,
      momentObj: null,
    });
    this.invalidDate = false;
    this.onChangeCallback(null);
    this.close();
    this.outputEvents.emit({type: "dateChanged", data: null});
  }

  isYearPicker(): boolean {
    return this.options.minView === "year";
  }

  isMonthPicker(): boolean {
    return this.options.minView === "month";
  }

  ngOnChanges(changes: SimpleChanges) {
    if ((changes["options"])) {
      this.ngOnInit();
    }
  }
}
